"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { LIVE_TOOLS, TOOL_CATEGORIES, type Tool, type ToolCategory } from "@/lib/tools";
import { track } from "@/lib/analytics";

const CATEGORY_ORDER: ToolCategory[] = ["calculator", "checker", "assessment", "document", "permit"];

function haystack(t: Tool): string {
  return `${t.name} ${t.description} ${t.eyebrow ?? ""} ${TOOL_CATEGORIES[t.category].label}`.toLowerCase();
}
function matches(hay: string, tokens: string[]): boolean {
  return tokens.every((tok) => hay.includes(tok));
}

export function ToolsExplorer() {
  const [active, setActive] = useState<ToolCategory | "all">("all");
  const [query, setQuery] = useState("");

  // Allow deep links like /tools/?cat=permit or /tools/?q=havs.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get("cat") as ToolCategory | null;
    if (cat && CATEGORY_ORDER.includes(cat)) setActive(cat);
    const q = params.get("q") || params.get("search") || "";
    if (q.trim()) setQuery(q.trim());
  }, []);

  const q = query.trim().toLowerCase();
  const tokens = q ? q.split(/\s+/) : [];
  const searching = tokens.length > 0;

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: LIVE_TOOLS.length };
    for (const cat of CATEGORY_ORDER) c[cat] = LIVE_TOOLS.filter((t) => t.category === cat).length;
    return c;
  }, []);

  // When searching, match the whole set (findability beats category scoping). With no
  // query, the category pill scopes the list; "all" shows the grouped, category-led view.
  const filtered = useMemo(
    () =>
      LIVE_TOOLS.filter((t) => {
        const queryOk = !searching || matches(haystack(t), tokens);
        const catOk = searching || active === "all" || t.category === active;
        return queryOk && catOk;
      }),
    [active, searching, q],
  );
  const resultCount = filtered.length;
  const grouped = !searching && active === "all";

  // Debounced search-intent capture — what people look for and what returns nothing.
  const lastTracked = useRef("");
  useEffect(() => {
    if (!searching || q.length < 2) return;
    const id = setTimeout(() => {
      if (lastTracked.current === q) return;
      lastTracked.current = q;
      track("tool_search", { query: q, results: resultCount });
      if (resultCount === 0) track("tool_search_no_results", { query: q });
    }, 650);
    return () => clearTimeout(id);
  }, [q, searching, resultCount]);

  const groups = grouped
    ? CATEGORY_ORDER.map((cat) => ({ cat, tools: LIVE_TOOLS.filter((t) => t.category === cat) })).filter((g) => g.tools.length > 0)
    : [];

  function onCardClick(e: React.MouseEvent) {
    const a = (e.target as HTMLElement).closest("a");
    if (!a) return;
    const href = a.getAttribute("href") || "";
    track("tool_card_clicked", { query: q, href, category: active });
  }

  return (
    <div className="bg-surface">
      {/* Filter bar */}
      <div className="border-b border-line bg-surface">
        <div className="mx-auto max-w-7xl px-5 py-6">
          <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tools — e.g. HAVS, permit, COSHH, CDM, first aid"
              aria-label="Search free health & safety tools"
              className="w-full rounded-xl border border-line bg-white px-4 py-3 text-base text-ink focus:border-emerald focus:outline-none"
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Pill label="All tools" count={counts.all} on={active === "all" && !searching} onClick={() => { setQuery(""); setActive("all"); }} />
            {CATEGORY_ORDER.map((cat) => (
              <Pill
                key={cat}
                label={TOOL_CATEGORIES[cat].label}
                count={counts[cat]}
                on={!searching && active === cat}
                onClick={() => {
                  setQuery("");
                  setActive(cat);
                  track("tool_filter_changed", { category: cat });
                }}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Grouped, category-led view (default) */}
      {grouped &&
        groups.map((group, i) => (
          <section key={group.cat} className={i % 2 === 0 ? "bg-mist" : "bg-surface"}>
            <div className="mx-auto max-w-7xl px-5 py-12">
              <h2 className="text-2xl md:text-3xl">{TOOL_CATEGORIES[group.cat].label}</h2>
              <p className="mt-2 max-w-2xl text-muted">{TOOL_CATEGORIES[group.cat].blurb}</p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" onClickCapture={onCardClick}>
                {group.tools.map((t) => <ToolCard key={t.slug} t={t} />)}
              </div>
            </div>
          </section>
        ))}

      {/* Flat, filtered/searched view */}
      {!grouped && (
        <section className="bg-mist">
          <div className="mx-auto max-w-7xl px-5 py-12">
            <p className="text-sm text-body">
              {resultCount} {resultCount === 1 ? "tool" : "tools"}
              {searching ? <> for &ldquo;{query.trim()}&rdquo;</> : <> · {TOOL_CATEGORIES[active as ToolCategory].label}</>}
            </p>
            {resultCount === 0 ? (
              <div className="mt-6 rounded-xl border border-line bg-white p-6 shadow-soft">
                <p className="font-display text-lg font-bold text-ink">No tools match &ldquo;{query.trim()}&rdquo; yet.</p>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-body">
                  Try a broader term, or build the full document in the{" "}
                  <Link href="/builder/" className="font-semibold text-emerald-deep underline">RAMS builder</Link>. Missing a tool you need?{" "}
                  <a href="mailto:hello@ramsdocs.co.uk" className="font-semibold text-emerald-deep underline">Tell us</a> — most-requested gets built first.
                </p>
              </div>
            ) : (
              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" onClickCapture={onCardClick}>
                {filtered.map((t) => <ToolCard key={t.slug} t={t} />)}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

function ToolCard({ t }: { t: Tool }) {
  return (
    <Link href={`/tools/${t.slug}/`} className="card lift shadow-soft p-6">
      {t.eyebrow && (
        <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">{t.eyebrow}</div>
      )}
      <div className="mt-1 font-display text-xl font-semibold text-ink">{t.name}</div>
      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-muted">{t.description}</p>
      <div className="mt-4 text-sm font-semibold text-emerald-deep">Use tool →</div>
    </Link>
  );
}

function Pill({ label, count, on, onClick }: { label: string; count: number; on: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
        on ? "border-emerald bg-emerald text-white" : "border-line bg-white text-body hover:border-line-2 hover:text-ink"
      }`}
    >
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}
